import { Reservation, Table } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { db } from 'utils/firebase';
import {
  buildTableBlockDoc,
  FULL_DAY_TABLE_BLOCK_TIME_FIELDS,
  groupTableBlocks,
  shiftTableBlockTimeFields,
  TableBlockTimeFields,
  tableBlockDocumentId,
  tableBlockWindowKey,
} from 'utils/tableBlocks';

/**
 * Turn the timeline state of the block dialog into Firestore writes: add what
 * became blocked, delete what became free. Shared by the table sidebar and the
 * timetable so both write the same documents. Commits everything as one
 * atomic batch and rejects on failure so callers can surface an error.
 */
const applyTableBlockChanges = ({
  fullDay,
  shiftIds,
  removeOtherIds,
  blocks,
  shifts,
  restaurantId,
  date,
  table,
  uid,
  now,
}: {
  fullDay: boolean;
  shiftIds: string[];
  removeOtherIds: string[];
  blocks: Reservation[];
  shifts: ReservationShift[];
  restaurantId: string;
  date: string;
  table: Pick<Table, 'name' | 'space' | 'occassions'>;
  uid: string;
  now: number;
}) => {
  // Same table filter as the dialog, so both classify blocks identically —
  // otherwise saving could delete a block the dialog never showed.
  const existing = groupTableBlocks(blocks, shifts, table);
  const collection = db.collection('requestsV03');
  // One atomic batch: splitting a full-day block into shift blocks must not
  // end half-done (day block deleted, shift blocks missing = table bookable).
  const batch = db.batch();

  const remove = (id: string) => batch.delete(collection.doc(id));

  const add = (timeFields: TableBlockTimeFields) =>
    batch.set(
      collection.doc(
        tableBlockDocumentId({
          restaurantId,
          date,
          tableName: table.name,
          timeFields,
        })
      ),
      buildTableBlockDoc({
        restaurantId,
        date,
        tableName: table.name,
        space: table.space,
        uid,
        now,
        timeFields,
      })
    );

  removeOtherIds.forEach(remove);

  if (fullDay && !existing.fullDay) add(FULL_DAY_TABLE_BLOCK_TIME_FIELDS);
  if (!fullDay && existing.fullDay) remove(existing.fullDay.id);

  // Shifts that run at the same time share one window, so collect windows and
  // not shift ids — otherwise three parallel shifts write three identical
  // documents, which then look like a triple booking.
  const wanted: Record<string, TableBlockTimeFields> = {};

  // A full-day block already covers every shift, so per-shift blocks are only
  // written when the day itself stays open.
  if (!fullDay) {
    shiftIds.forEach((shiftId) => {
      const timeFields = shiftTableBlockTimeFields(shifts, shiftId);
      if (timeFields) wanted[tableBlockWindowKey(timeFields)] = timeFields;
    });
  }

  Object.keys(wanted).forEach((key) => {
    if (!existing.byWindowKey[key]) add(wanted[key]);
  });

  Object.keys(existing.byWindowKey).forEach((key) => {
    if (!wanted[key]) remove(existing.byWindowKey[key].id);
  });

  return batch.commit();
};

/**
 * The default path without the experimental flag: one click blocks the whole
 * day, one click frees the table again. Staff mid-service must not pay a
 * dialog for that. Every block on the table goes, so a shift block left over
 * from the dialog cannot survive under the new day block. `blocked` is the
 * state the user sees in the shift they are looking at, and the click reacts
 * to that, not to blocks in shifts that are currently out of view.
 */
export const toggleFullDayTableBlock = ({
  blocked,
  blocks,
  restaurantId,
  date,
  table,
  uid,
  now,
}: {
  blocked: boolean;
  blocks: Reservation[];
  restaurantId: string;
  date: string;
  table: Pick<Table, 'name' | 'space'>;
  uid: string;
  now: number;
}) => {
  const collection = db.collection('requestsV03');
  const batch = db.batch();

  blocks.forEach((block) => batch.delete(collection.doc(block.id)));

  if (!blocked) {
    batch.set(
      collection.doc(
        tableBlockDocumentId({
          restaurantId,
          date,
          tableName: table.name,
          timeFields: FULL_DAY_TABLE_BLOCK_TIME_FIELDS,
        })
      ),
      buildTableBlockDoc({
        restaurantId,
        date,
        tableName: table.name,
        space: table.space,
        uid,
        now,
        timeFields: FULL_DAY_TABLE_BLOCK_TIME_FIELDS,
      })
    );
  }

  return batch.commit();
};

export const removeTableBlock = (blockId: string) =>
  db.collection('requestsV03').doc(blockId).delete();

export default applyTableBlockChanges;
